import React, { useState, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Stack, FormControl,
  InputLabel, Select, MenuItem, Grid, Paper, Divider, Avatar,
  List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
  CheckCircle as AvailableIcon,
  Cancel as UnavailableIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FleetVehicle } from '../types';
import {
  useAllVehicles,
  useCalendarView,
  useCalendarConflicts,
  usePeakUsage,
} from '../hooks/useFleetData';

interface VehicleWithAvailability extends FleetVehicle {
  availability: 'available' | 'unavailable';
  conflictingRequests: Array<{
    requestId: string; purpose: string;
    startTime: string; endTime: string; requester: string;
  }>;
}

const fmtDate = (d: string | Date) =>
  new Date(d).toLocaleDateString();

const fmtTime = (d: string) =>
  new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const VehicleCalendarScheduler: React.FC = () => {
  const [selectedDate,     setSelectedDate]     = useState<Date>(new Date());
  const [selectedCategory, setSelectedCategory] = useState<'all'|'executive'|'staff_bus'|'technical'|'general'>('all');
  const [selectedVehicle,  setSelectedVehicle]  = useState<VehicleWithAvailability | null>(null);
  const [conflictDialog,   setConflictDialog]   = useState(false);

  // ── Compute stable ISO strings for query keys ─────────────────────────────
  // React Query only refetches when keys change — so if the date doesn't change,
  // switching tabs and back does NOT trigger a reload.
  const dayStart = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, [selectedDate]);

  const dayEnd = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }, [selectedDate]);

  const businessStart = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(8, 0, 0, 0);
    return d.toISOString();
  }, [selectedDate]);

  const businessEnd = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(18, 0, 0, 0);
    return d.toISOString();
  }, [selectedDate]);

  const peakStart = useMemo(() => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  }, [selectedDate]);

  const peakEnd = useMemo(() => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 7);
    return d.toISOString();
  }, [selectedDate]);

  // ── React Query hooks — all cached ────────────────────────────────────────
  const categoryParam = selectedCategory === 'all' ? undefined : selectedCategory;

  const {
    data: allVehicles   = [],
    isLoading: vLoading,
    isError:   vError,
    refetch:   refetchV,
  } = useAllVehicles(categoryParam ? { category: categoryParam } : undefined);

  const {
    data: calendarEvents = [],
  } = useCalendarView(dayStart, dayEnd);

  const {
    data: conflicts = [],
  } = useCalendarConflicts(businessStart, businessEnd);

  const {
    data: peakData,
  } = usePeakUsage(peakStart, peakEnd);

  // ── Compute availability from cached conflict data ────────────────────────
  const vehicles: VehicleWithAvailability[] = useMemo(() => {
    const conflictMap = new Map<string, typeof conflicts[0][]>();

    conflicts.forEach(c => {
      const plate = c.assignedVehicle?.plateNumber;
      if (plate) {
        if (!conflictMap.has(plate)) conflictMap.set(plate, []);
        conflictMap.get(plate)!.push(c);
      }
    });

    const result = allVehicles.map(v => {
      const vehicleConflicts = conflictMap.get(v.plateNumber) || [];
      return {
        ...v,
        availability: vehicleConflicts.length > 0 ? 'unavailable' : 'available',
        conflictingRequests: vehicleConflicts.map(c => ({
          requestId: c._id,
          purpose:   c.purpose,
          startTime: c.startDateTime,
          endTime:   c.endDateTime,
          requester: c.requestedBy
            ? `${c.requestedBy.firstName} ${c.requestedBy.lastName}`
            : 'Portal Request',
        })),
      } as VehicleWithAvailability;
    });

    // Available first
    return result.sort((a, b) =>
      a.availability === b.availability ? 0 : a.availability === 'available' ? -1 : 1
    );
  }, [allVehicles, conflicts]);

  const available = vehicles.filter(v => v.availability === 'available');
  const busy      = vehicles.filter(v => v.availability === 'unavailable');

  // ── Peak usage helpers ────────────────────────────────────────────────────
  const getHourColor = (hour: number): any => {
    if (!peakData?.hourlyDistribution) return 'success';
    const h = peakData.hourlyDistribution.find(x => x.hour === hour);
    if (!h) return 'success';
    const max = Math.max(...peakData.hourlyDistribution.map(x => x.requestCount));
    if (max === 0) return 'success';
    const pct = h.requestCount / max;
    return pct > 0.8 ? 'error' : pct > 0.5 ? 'warning' : 'success';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" fontWeight="bold">Vehicle Schedule & Availability</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Vehicle Category</InputLabel>
            <Select value={selectedCategory} label="Vehicle Category" onChange={e => setSelectedCategory(e.target.value as any)}>
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="executive">Executive</MenuItem>
              <MenuItem value="staff_bus">Staff Bus</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select Date" value={selectedDate}
              onChange={v => setSelectedDate(v || new Date())}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Left — peak hours + overview */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Peak Usage Hours</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Best times to schedule based on historical data
              </Typography>
              {peakData ? (
                <>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                    {[8,9,10,11,12,13,14,15,16,17].map(h => (
                      <Chip key={h} size="small" color={getHourColor(h)} label={`${h}:00`}
                        variant={getHourColor(h) === 'success' ? 'outlined' : 'filled'} sx={{ minWidth: 60 }} />
                    ))}
                  </Stack>
                  <Alert severity="info">
                    <Typography variant="caption">
                      Peak hour: {peakData.insights.peakHour.timeLabel} ({peakData.insights.peakHour.requestCount} requests)
                    </Typography>
                  </Alert>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">No historical data yet.</Typography>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Today's Schedule Overview</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{fmtDate(selectedDate)}</Typography>
              <Stack spacing={1.5}>
                {[
                  { label: 'Total Bookings',     value: calendarEvents.length, color: 'text.primary'  },
                  { label: 'Available Vehicles', value: available.length,      color: 'success.main'  },
                  { label: 'Busy Vehicles',      value: busy.length,           color: busy.length > 0 ? 'error.main' : 'text.secondary' },
                ].map(r => (
                  <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{r.label}:</Typography>
                    <Typography variant="body2" fontWeight="bold" color={r.color}>{r.value}</Typography>
                  </Box>
                ))}
              </Stack>
              {busy.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    Busy = vehicle has a fleet-approved assignment during business hours (8am–6pm).
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right — vehicle list */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  Vehicle Availability — {fmtDate(selectedDate)}
                </Typography>
                <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetchV()} disabled={vLoading}>
                  Refresh
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click a vehicle to see its scheduled trips
              </Typography>

              {vError && (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" color="inherit" onClick={() => refetchV()}>Retry</Button>}>
                  Failed to load vehicles. Check your connection.
                </Alert>
              )}

              {vLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">Loading vehicle availability...</Typography>
                </Box>
              ) : vehicles.length === 0 && !vError ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <CarIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>No vehicles found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedCategory !== 'all'
                      ? `No ${selectedCategory} vehicles registered. Try "All Categories".`
                      : 'No vehicles have been added to this company yet.'}
                  </Typography>
                  <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => refetchV()}>
                    Retry
                  </Button>
                </Paper>
              ) : (
                <Box>
                  {available.map(v => (
                    <VehicleCard key={v.id} vehicle={v}
                      onClick={() => { setSelectedVehicle(v); }} />
                  ))}

                  {available.length > 0 && busy.length > 0 && (
                    <Divider sx={{ my: 2 }}><Chip label="Busy Today" size="small" color="error" variant="outlined" /></Divider>
                  )}

                  {busy.map(v => (
                    <VehicleCard key={v.id} vehicle={v}
                      onClick={() => { setSelectedVehicle(v); setConflictDialog(true); }} />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Schedule dialog */}
      <Dialog open={conflictDialog} onClose={() => setConflictDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Scheduled Trips — {selectedVehicle?.plateNumber}
          <Typography variant="subtitle2" color="text.secondary">
            {selectedVehicle?.make} {selectedVehicle?.model}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedVehicle?.conflictingRequests?.length ? (
            <List>
              {selectedVehicle.conflictingRequests.map((c, i) => (
                <React.Fragment key={c.requestId}>
                  {i > 0 && <Divider />}
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'warning.main' }}><ScheduleIcon /></Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={c.purpose}
                      secondary={
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" />
                            <Typography variant="caption">{c.requester}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScheduleIcon fontSize="small" />
                            <Typography variant="caption">{fmtTime(c.startTime)} – {fmtTime(c.endTime)}</Typography>
                          </Box>
                        </Stack>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Chip size="small" color="warning" label="Booked" />
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Alert severity="success">
              This vehicle is available for {fmtDate(selectedDate)}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflictDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Vehicle Card ──────────────────────────────────────────────────────────────
const VehicleCard: React.FC<{ vehicle: VehicleWithAvailability; onClick: () => void }> = ({ vehicle, onClick }) => {
  const isAvailable = vehicle.availability === 'available';
  return (
    <Card
      sx={{
        mb: 1.5, cursor: 'pointer', border: '1px solid',
        borderColor: isAvailable ? 'divider' : 'error.light',
        transition: 'box-shadow 0.15s', '&:hover': { boxShadow: 3 },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: '12px 16px !important' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: isAvailable ? 'success.main' : 'error.main', width: 38, height: 38 }}>
            {isAvailable ? <AvailableIcon /> : <UnavailableIcon />}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {vehicle.make} {vehicle.model}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {vehicle.plateNumber} · {vehicle.category}
              {vehicle.seatingCapacity ? ` · ${vehicle.seatingCapacity} seats` : ''}
            </Typography>
            {!isAvailable && vehicle.conflictingRequests.length > 0 && (
              <Typography variant="caption" color="error.main" display="block">
                {vehicle.conflictingRequests.length} booking(s) today — click to view
              </Typography>
            )}
          </Box>
          <Chip size="small" color={isAvailable ? 'success' : 'error'} label={isAvailable ? 'Available' : 'Busy'} />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default VehicleCalendarScheduler;